'use client';

import {getOnboardingSummary} from 'iq-onboarding/api';
import {GuidesCard} from 'iq-onboarding/components/guides_card';
import {QuestionsCard} from 'iq-onboarding/components/questions_card';
import type {OnboardingCompany, OnboardingPosition, OnboardingSummary} from 'iq-onboarding/types';
import {trackSummaryContinued, trackSummaryReview} from 'iq-onboarding/tracking';
import {useEffect, useState} from 'react';

interface SummaryStepProps {
  positions: OnboardingPosition[];
  companies: OnboardingCompany[];
  onContinue: () => void;
  onReview: () => void;
}

export function SummaryStep({positions, companies, onContinue, onReview}: SummaryStepProps) {
  const [data, setData] = useState<OnboardingSummary | null>(null);

  useEffect(() => {
    let active = true;
    getOnboardingSummary(
      companies.map((c) => c.slug),
      positions.map((p) => p.slug),
    )
      .then((res) => {
        if (!active) return;
        // Fail-safe: nothing to showcase, skip straight to sign up. Shouldn't
        // happen in production for a valid company/position selection.
        if (res.questions.length === 0 || res.guides.length === 0) {
          onContinue();
          return;
        }
        setData(res);
      })
      .catch(() => active && onContinue());
    return () => {
      active = false;
    };
  }, [companies, positions, onContinue]);

  return (
    <div className='flex flex-col'>
      <div className='flex flex-col gap-5'>
        <div className='text-center'>
          <h1 className='text-2xl sm:text-3xl font-extrabold text-white'>
            Everything you need to ace your interviews
          </h1>
          <p className='text-white/60 text-base mt-2'>
            A preview of what Interview Query unlocks — questions and guides are just the start.
          </p>
        </div>

        <div className='grid gap-5 lg:grid-cols-2'>
          {data ? (
            <>
              <QuestionsCard questions={data.questions} totalCount={data.questions_count} />
              <GuidesCard guides={data.guides} totalCount={data.guides_total_count} />
            </>
          ) : (
            <>
              <SummaryCardSkeleton />
              <SummaryCardSkeleton />
            </>
          )}
        </div>
      </div>

      <div className='sticky bottom-0 z-10 mt-12 bg-iq-black'>
        <div className='pointer-events-none absolute inset-x-0 bottom-full h-12 bg-gradient-to-t from-iq-black to-transparent' />
        <div className='mx-auto flex w-full max-w-4xl flex-col gap-3 px-8 pb-5'>
          <button
            type='button'
            onClick={() => {
              void trackSummaryContinued(
                positions.map((p) => p.slug),
                companies.map((c) => c.slug),
              );
              onContinue();
            }}
            className='w-full py-4 px-8 bg-iq-orange text-white text-base font-bold rounded-full hover:opacity-90 transition-opacity'>
            Continue to sign up
          </button>
          <button
            type='button'
            onClick={() => {
              void trackSummaryReview();
              onReview();
            }}
            className='text-sm font-semibold text-white/60 hover:text-white transition-colors'>
            Review your career preferences
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCardSkeleton() {
  return (
    <div className='bg-white rounded-2xl p-5 flex flex-col gap-4 h-full animate-pulse'>
      <div className='h-5 w-1/3 bg-iq-grey-light rounded' />
      <div className='h-3 w-2/3 bg-iq-grey-lighter rounded' />
      <div className='h-8 w-1/2 bg-iq-grey-light rounded' />
      <div className='flex flex-col gap-3'>
        {['r1', 'r2', 'r3'].map((k) => (
          <div key={k} className='h-24 w-full bg-iq-grey-lighter rounded-lg' />
        ))}
      </div>
    </div>
  );
}
